"use client";

import { cn } from "@/lib/utils";

interface GlassContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  intensity?: 'light' | 'medium' | 'heavy';
}

const intensityMap = {
  light: {
    blur: 'backdrop-blur-[3px]',
    opacity: 'bg-white/5',
    gradient: 'rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%'
  },
  medium: {
    blur: 'backdrop-blur-[5px]',
    opacity: 'bg-white/10',
    gradient: 'rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 100%'
  },
  heavy: {
    blur: 'backdrop-blur-[8px]',
    opacity: 'bg-white/15',
    gradient: 'rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.15) 100%'
  }
};

export function GlassContainer({ 
  children, 
  className,
  intensity = 'medium',
  ...props 
}: GlassContainerProps) {
  const intensityStyles = intensityMap[intensity];

  return (
    <div 
      className={cn(
        intensityStyles.blur,
        "rounded-[32px]",
        "p-8 sm:p-10",
        "border-[2px]",
        "border-white/40",
        "shadow-lg",
        "relative",
        "isolation",
        "overflow-hidden",
        "transform-gpu", // Force GPU acceleration
        className
      )}
      style={{
        transform: 'translate3d(0, 0, 0)',
        backfaceVisibility: 'hidden',
        background: `
          linear-gradient(145deg, ${intensityStyles.gradient}),
          linear-gradient(to right,
            rgba(255,255,255,0.15) 0%,
            rgba(255,255,255,0.2) 50%,
            rgba(255,255,255,0.15) 100%
          )
        `,
        willChange: 'transform', // Hint to browser about animations
      }}
      {...props}
    >
      <div 
        className={cn(
          "absolute inset-0 backdrop-blur-[12px]",
          intensityStyles.opacity
        )}
        style={{
          maskImage: 'linear-gradient(to bottom, transparent, black)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black)',
        }}
        aria-hidden="true"
      />
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
} 